/**
 * Generic JSON-LD builders for non-city / non-service-template pages.
 *
 * Returns `<script type="application/ld+json">` entries you can drop into a
 * route's `head().scripts`. Covers:
 *   - WebPage (with optional FAQPage merged in)
 *   - BreadcrumbList (Home → optional Parent → Current)
 *
 * For city pages use `cityHeadScripts` (cityJsonLd.ts) and for major service
 * pages use `serviceHeadScripts` (serviceJsonLd.ts) — those emit richer
 * Service / LocalBusiness / AggregateRating blocks.
 */

import { canonicalUrl, SITE_URL, SITE_NAME } from "@/lib/seo";

export interface PageJsonLdInput {
  path: string;
  name: string;
  description: string;
  /** Optional intermediate breadcrumb crumb (e.g. { name: "Services", path: "/services" }). */
  parent?: { name: string; path: string };
  /** Optional FAQ entries — emit a FAQPage block when 1+ provided. */
  faqs?: { q: string; a: string }[];
}

export function pageHeadScripts(input: PageJsonLdInput) {
  const url = canonicalUrl(input.path);

  const crumbs = [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
  ] as Array<Record<string, unknown>>;
  let pos = 2;
  if (input.parent) {
    crumbs.push({
      "@type": "ListItem",
      position: pos++,
      name: input.parent.name,
      item: canonicalUrl(input.parent.path),
    });
  }
  crumbs.push({
    "@type": "ListItem",
    position: pos,
    name: input.name,
    item: url,
  });

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs,
  };

  const webpage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${url}#webpage`,
    name: input.name,
    description: input.description,
    url,
    isPartOf: { "@type": "WebSite", "@id": `${SITE_URL}/#website`, name: SITE_NAME, url: SITE_URL },
    publisher: { "@id": `${SITE_URL}/#business` },
    breadcrumb: { "@id": `${url}#breadcrumb` },
  };

  const scripts: { type: string; children: string }[] = [
    { type: "application/ld+json", children: JSON.stringify(webpage) },
    { type: "application/ld+json", children: JSON.stringify({ ...breadcrumb, "@id": `${url}#breadcrumb` }) },
  ];

  if (input.faqs && input.faqs.length > 0) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        mainEntity: input.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    });
  }

  return scripts;
}
